'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentViewer, GeneralNotFound } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, EventEmitter, guardedClient as request } from '@repo/shared/helpers';
import { getUserDocument } from 'src/api/admin/documents';
import { getUserDocumentTypes } from 'src/api/admin/documentTypes';
import { getAllUserTasks } from 'src/api/admin/tasks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/users/$userId/documents/$documentId')({
	loader: async ({ params }) => {
		const { userId, documentId } = params;
		const [document, documentTypes, userTasks] = await Promise.all([
			getUserDocument(userId, documentId),
			getUserDocumentTypes(userId),
			getAllUserTasks(userId),
		]);
		return { document, documentTypes, userTasks };
	},
	component: DocumentDetailPage,
});

function DocumentDetailPage() {
	const { userId, documentId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		document: initialDocument,
		documentTypes: initialDocumentTypes,
		userTasks: initialUserTasks,
	} = Route.useLoaderData();
	console.log(initialDocument);
	const { user: authenticatedUser } = useAuthenticatedUser();

	console.log(initialDocumentTypes);
	const { data: document, refetch: refetchDocument } = useQuery({
		queryKey: ['user-document', userId, documentId],
		queryFn: () => getUserDocument(userId, documentId),
		initialData: initialDocument,
	});

	const { data: documentTypes } = useQuery({
		queryKey: ['document-types', 'users'],
		queryFn: () => getUserDocumentTypes(userId),
		initialData: initialDocumentTypes,
	});

	const { data: userTasks } = useQuery({
		queryKey: ['user-all-tasks', userId],
		queryFn: () => getAllUserTasks(userId),
		initialData: initialUserTasks,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const handleUpdate = () => {
		refetchDocument();
		queryClient.invalidateQueries({ queryKey: ['user-documents', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
	};

	const handleClose = () => {
		navigate({ to: '/users/$userId/documents', params: { userId }, search: { filter: undefined } });
	};

	const forceParseDoc = async (doc?: typeof document) => {
		if (!doc) return;
		try {
			await request.post(`/api/users/${userId}/files/${doc._id}/force-reparse`, doc);
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to update document.',
			});
		}
		handleUpdate();
	};

	if (!document) {
		return (
			<GeneralNotFound
				label="We couldn't find this document. Maybe it has been deleted?"
				action={{
					label: 'Return to documents',
					href: `/users/${userId}/documents`,
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={documentTypes?.data || []}
			apiUrlBase={`/api/users/${userId}/files`}
			onRevalidateDocument={handleUpdate}
			onForceParse={authenticatedUser?.isSystemAdmin ? forceParseDoc : undefined}
			tasks={userTasks || []}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.adminUserdocumentmetadataView]),
				fullView: verifyPermission([PERMISSIONS.adminUserdocumentsView]),
				sensitiveView: verifyPermission([PERMISSIONS.adminSensitiveUserdocumentsView]),
				sensitiveEdit: verifyPermission([PERMISSIONS.adminSensitiveUserdocumentsEdit]),
				sensitiveArchive: verifyPermission([PERMISSIONS.adminSensitiveUserdocumentsArchive]),
				edit: verifyPermission([PERMISSIONS.adminUserdocumentsEdit]),
				archive: verifyPermission([PERMISSIONS.adminUserdocumentsArchive]),
				review: verifyPermission([PERMISSIONS.adminUserdocumentsReview]),
				sensitiveReview: verifyPermission([PERMISSIONS.adminSensitiveUserdocumentsReview]),
			}}
		/>
	);
}
